/**
 * CadPropertiesBody — the schema-driven body the Properties panel renders
 * for `cad:*` selections (Faz 5.5/5.6/5.7). Subscribes to the selection and
 * the entity map itself (panels own their store subscriptions), resolves the
 * schema sections and renders them as collapsible blocks. Bulk edits commit
 * through undoablePatch: ONE history entry across every selected entity.
 */

import { Box, Flex, Text, VStack } from '@chakra-ui/react';
import { ChevronDown, ChevronRight } from 'lucide-react';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAppStore } from '../../../state/store';
import { undoablePatch } from '../../../core/commands/entityOps';
import { buildSections, isCadSchemaEntity, type PropSection } from './schema';
import { PropRowView } from './PropRowView';

/** One collapsible schema section (OCS PropSection parity). */
function SectionBlock({ section, defaultOpen = true }: { section: PropSection; defaultOpen?: boolean }) {
    const { t } = useTranslation();
    const [open, setOpen] = useState(defaultOpen);
    return (
        <Box minW={0}>
            <Flex
                as="button"
                role="button"
                aria-expanded={open}
                align="center"
                gap={1}
                w="full"
                py={1}
                px={1}
                borderRadius="4px"
                _hover={{ bg: 'bg.hover' }}
                onClick={() => setOpen((v) => !v)}
                flexShrink={0}
            >
                {open ? <ChevronDown size={12} /> : <ChevronRight size={12} />}
                <Text fontSize="xs" fontWeight="semibold" color="text.primary" noOfLines={1}>
                    {t(section.titleKey as never)}
                </Text>
            </Flex>
            {open && (
                <VStack spacing={1.5} align="stretch" pl={1} pb={2} minW={0}>
                    {section.rows.map((row) => (
                        <PropRowView key={row.id} row={row} />
                    ))}
                </VStack>
            )}
        </Box>
    );
}

export function CadPropertiesBody() {
    const { t } = useTranslation();
    const selectedIds = useAppStore((s) => s.selectedIds);
    const entityMap = useAppStore((s) => s.entities);
    const [vertexIndex, setVertexIndex] = useState(0);

    const entities = useMemo(
        () => selectedIds.map((id) => entityMap[id]).filter((e): e is NonNullable<typeof e> => !!e && isCadSchemaEntity(e)),
        [selectedIds, entityMap],
    );

    const ctx = useMemo(() => ({
        commit: (patch: (entity: import('../../../state/store').Entity) => Partial<import('../../../state/store').Entity> | null) => {
            undoablePatch(selectedIds, patch);
        },
        vertexIndex,
        setVertexIndex,
        layers: useAppStore.getState().layers,
    }), [selectedIds, vertexIndex]);

    const sections = useMemo(() => buildSections(entities, ctx), [entities, ctx]);

    if (entities.length === 0) return null;

    return (
        <VStack spacing={2} align="stretch" minW={0}>
            {entities.length > 1 && (
                <Text fontSize="xs" color="text.secondary" px={1}>
                    {t('ui.cad.props.selectedCount', { n: entities.length })}
                </Text>
            )}
            {sections.map((section) => (
                <SectionBlock key={section.id} section={section} />
            ))}
        </VStack>
    );
}
